import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/format";

export type DateRangeValue = { from: Date | null; to: Date | null };

export type DateRangePreset = {
  key: string;
  label: string;
  resolve: () => DateRangeValue;
};

export type DateRangePickerProps = {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  presets?: DateRangePreset[];
  align?: "start" | "center" | "end";
  className?: string;
  placeholder?: string;
  testId?: string;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

export const DEFAULT_PRESETS: DateRangePreset[] = [
  {
    key: "today",
    label: "Today",
    resolve: () => {
      const t = startOfDay(new Date());
      return { from: t, to: endOfDay(t) };
    },
  },
  {
    key: "last_7",
    label: "Last 7 days",
    resolve: () => {
      const to = endOfDay(new Date());
      const from = startOfDay(new Date());
      from.setDate(from.getDate() - 6);
      return { from, to };
    },
  },
  {
    key: "last_30",
    label: "Last 30 days",
    resolve: () => {
      const to = endOfDay(new Date());
      const from = startOfDay(new Date());
      from.setDate(from.getDate() - 29);
      return { from, to };
    },
  },
  {
    key: "this_month",
    label: "This month",
    resolve: () => {
      const now = new Date();
      const from = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
      return { from, to: endOfDay(now) };
    },
  },
  {
    key: "last_month",
    label: "Last month",
    resolve: () => {
      const now = new Date();
      const from = startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const to = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
      return { from, to };
    },
  },
  {
    key: "this_year",
    label: "This year",
    resolve: () => {
      const now = new Date();
      const from = startOfDay(new Date(now.getFullYear(), 0, 1));
      return { from, to: endOfDay(now) };
    },
  },
];

function rangeLabel(value: DateRangeValue, placeholder: string): string {
  if (!value.from && !value.to) return placeholder;
  if (value.from && value.to) {
    if (value.from.toDateString() === value.to.toDateString()) {
      return formatShortDate(value.from);
    }
    return `${formatShortDate(value.from)} → ${formatShortDate(value.to)}`;
  }
  if (value.from) return `From ${formatShortDate(value.from)}`;
  return `To ${formatShortDate(value.to)}`;
}

export function DateRangePicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  align = "start",
  className,
  placeholder = "Select range",
  testId,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo<DateRange | undefined>(() => {
    if (!value.from && !value.to) return undefined;
    return { from: value.from ?? undefined, to: value.to ?? undefined };
  }, [value]);

  const handleCalendarChange = (range: DateRange | undefined) => {
    onChange({
      from: range?.from ? startOfDay(range.from) : null,
      to: range?.to ? endOfDay(range.to) : null,
    });
  };

  const applyPreset = (preset: DateRangePreset) => {
    onChange(preset.resolve());
    setOpen(false);
  };

  const isActive = !!(value.from || value.to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn(
            "h-8 text-xs gap-1.5 justify-start font-normal",
            isActive && "border-primary/40 bg-primary/5",
            className,
          )}
          data-testid={testId}
        >
          <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
          <span className={cn(!isActive && "text-muted-foreground")}>
            {rangeLabel(value, placeholder)}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="p-0 w-auto" sideOffset={4}>
        <div className="flex flex-col sm:flex-row">
          <div className="flex sm:flex-col gap-1 p-2 sm:border-r border-b sm:border-b-0 min-w-[8rem]">
            {presets.map(preset => (
              <Button
                key={preset.key}
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs justify-start font-normal"
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div>
            <Calendar
              mode="range"
              selected={selected}
              onSelect={handleCalendarChange}
              numberOfMonths={2}
              defaultMonth={value.from ?? new Date()}
            />
            {isActive ? (
              <div className="flex justify-end p-2 border-t">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => {
                    onChange({ from: null, to: null });
                    setOpen(false);
                  }}
                >
                  Clear
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePicker;
